// context/ThemeContext.js
// Tema warna aksen aplikasi. Nilai preset ditulis ke CSS variable --brand-*
// di <html>, sehingga seluruh kelas brand-* (lihat tailwind.config.js) langsung
// ikut berubah tanpa rebuild.
//
// Nilai dalam format channel RGB "R G B" (tanpa rgb()/koma) — wajib, supaya
// modifier opacity Tailwind seperti bg-brand-600/30 tetap bekerja.
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'app.theme';
const MODE_KEY = 'app.mode';
// Default berlaku untuk perangkat yang BELUM pernah memilih (localStorage kosong
// dan users.theme masih NULL) — termasuk halaman login, karena di sana belum ada
// user. User yang sudah memilih warna lain tidak terpengaruh.
// Kalau diubah, ganti juga blok :root di index.css agar tidak ada kedip warna.
const DEFAULT_THEME = 'amber';

// Diambil dari palet bawaan Tailwind agar tangga kontrasnya sudah teruji.
export const THEME_PRESETS = {
  blue: {
    labelKey: 'theme.blue', swatch: '#2563eb',
    scale: {
      50: '239 246 255', 100: '219 234 254', 200: '191 219 254', 300: '147 197 253',
      400: '96 165 250', 500: '59 130 246', 600: '37 99 235', 700: '29 78 216',
      800: '30 64 175', 900: '30 58 138',
    },
  },
  indigo: {
    labelKey: 'theme.indigo', swatch: '#4f46e5',
    scale: {
      50: '238 242 255', 100: '224 231 255', 200: '199 210 254', 300: '165 180 252',
      400: '129 140 248', 500: '99 102 241', 600: '79 70 229', 700: '67 56 202',
      800: '55 48 163', 900: '49 46 129',
    },
  },
  teal: {
    labelKey: 'theme.teal', swatch: '#0d9488',
    scale: {
      50: '240 253 250', 100: '204 251 241', 200: '153 246 228', 300: '94 234 212',
      400: '45 212 191', 500: '20 184 166', 600: '13 148 136', 700: '15 118 110',
      800: '17 94 89', 900: '19 78 74',
    },
  },
  emerald: {
    labelKey: 'theme.emerald', swatch: '#059669',
    scale: {
      50: '236 253 245', 100: '209 250 229', 200: '167 243 208', 300: '110 231 183',
      400: '52 211 153', 500: '16 185 129', 600: '5 150 105', 700: '4 120 87',
      800: '6 95 70', 900: '6 78 59',
    },
  },
  violet: {
    labelKey: 'theme.violet', swatch: '#7c3aed',
    scale: {
      50: '245 243 255', 100: '237 233 254', 200: '221 214 254', 300: '196 181 253',
      400: '167 139 250', 500: '139 92 246', 600: '124 58 237', 700: '109 40 217',
      800: '91 33 182', 900: '76 29 149',
    },
  },
  amber: {
    labelKey: 'theme.amber', swatch: '#e2a03f',
    scale: {
      50: '254 248 236', 100: '252 240 216', 200: '248 226 178', 300: '243 210 138',
      400: '235 187 92', 500: '232 174 71', 600: '226 160 63', 700: '189 130 44',
      800: '152 103 35', 900: '120 81 28',
    },
  },
  rose: {
    labelKey: 'theme.rose', swatch: '#e11d48',
    scale: {
      50: '255 241 242', 100: '255 228 230', 200: '254 205 211', 300: '253 164 175',
      400: '251 113 133', 500: '244 63 94', 600: '225 29 72', 700: '190 18 60',
      800: '159 18 57', 900: '136 19 55',
    },
  },
  black: {
    labelKey: 'theme.black', swatch: '#18181b',
    scale: {
      50: '250 250 250', 100: '244 244 245', 200: '228 228 231', 300: '212 212 216',
      400: '82 82 91', 500: '63 63 70', 600: '39 39 42', 700: '24 24 27',
      800: '18 18 20', 900: '9 9 11',
    },
  },
};

// Mode gelap: tangga terang --brand-50/100/200 dipakai sebagai latar tint
// (hover baris tabel, chip, tab aktif). Di atas latar gelap nilainya diganti
// campuran brand-500 dengan permukaan gelap, supaya teks terang tetap kontras.
const DARK_SURFACE = [20, 29, 47];
const DARK_TINT = { 50: 0.16, 100: 0.24, 200: 0.34 };
export function applyTheme(key, mode) {
  const preset = THEME_PRESETS[key] || THEME_PRESETS[DEFAULT_THEME];
  const root = document.documentElement;
  const c500 = preset.scale[500].split(' ').map(Number);
  Object.entries(preset.scale).forEach(([step, rgb]) => {
    let v = rgb;
    if (mode === 'dark' && DARK_TINT[step] != null) {
      const a = DARK_TINT[step];
      v = c500.map((c, i) => Math.round(c * a + DARK_SURFACE[i] * (1 - a))).join(' ');
    }
    root.style.setProperty(`--brand-${step}`, v);
  });
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return THEME_PRESETS[saved] ? saved : DEFAULT_THEME;
    } catch (_) {
      return DEFAULT_THEME;
    }
  });

  // Terapkan saat mount dan setiap kali tema berubah.


  const setTheme = useCallback((key) => {
    if (!THEME_PRESETS[key]) return;
    setThemeState(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch (_) {}
  }, []);

  // ── Mode terang/gelap ─────────────────────────────────────────────────────
  // Terpisah dari warna aksen: aksen tetap dipilih bebas, mode hanya memasang
  // class `dark` di <html> → dipetakan oleh src/dark.css. Disimpan per perangkat
  // (localStorage 'app.mode'); public/index.html membacanya lebih awal agar tidak kedip.
  const [mode, setModeState] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) === 'dark' ? 'dark' : 'light'; }
    catch (_) { return 'light'; }
  });
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', mode === 'dark');
    applyTheme(theme, mode);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', mode === 'dark' ? '#0b1220' : '#1e293b');
  }, [mode, theme]);
  const setMode = useCallback((m) => {
    const v = m === 'dark' ? 'dark' : 'light';
    setModeState(v);
    try { localStorage.setItem(MODE_KEY, v); } catch (_) {}
  }, []);

  // Dipakai saat hidrasi dari server (users.theme). Tidak memicu penulisan balik
  // ke API — hanya menyelaraskan state lokal dengan nilai yang sudah tersimpan.
  // Nilai null/tidak dikenal diabaikan, artinya user belum pernah memilih dan
  // preferensi lokal (atau default) tetap dipakai.
  const hydrateTheme = useCallback((key) => {
    if (!key || !THEME_PRESETS[key]) return;
    setThemeState(prev => (prev === key ? prev : key));
    try { localStorage.setItem(STORAGE_KEY, key); } catch (_) {}
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, hydrateTheme, presets: THEME_PRESETS, mode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme harus dipakai di dalam <ThemeProvider>');
  return ctx;
}
